import { ComponentList } from '../../components/core/component-list.js';
import { getFormattedName, getInitialsFromName } from '../../utility/general-utility.js';
import { MapUtils } from '../../utility/map-utils.js';
import { cloneData, cloneDataOrNull, DataModelBuilder, isSameData, SerializationContext, SerializedData } from '../dto/dto.js';
import { PhoneNumber } from './phone-number.js';
import { PhotoReference } from './file-reference/photo-reference.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { PartnerProfileBase, PartnerProfileStatus } from '../partner-profile-base.js';
import { fromEnumAsInt, fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { VersionRange } from '../core/version-range.js';
import { MembershipRole } from './membership-role.js';

export class CollaboratorProfile extends PartnerProfileBase {
  static readonly TYPE_ID: string = 'b8cbbe732fda4a5b89fb3eec12190424';
  static readonly SCHEMA_VERSION: number = 2;

  public readonly partnerRole: MembershipRole = MembershipRole.Collaborator;
  public readonly formattedName: string;
  public readonly initials: string;

  private constructor(
    public readonly uniqueId: string,
    public readonly businessRef: BusinessModuleRef,
    public readonly firstName: string,
    public readonly lastName: string,
    public readonly phoneNumber: PhoneNumber,
    public readonly photo: PhotoReference | null,
    public readonly userId: string | null,
    public readonly pushNotificationToken: string | null,
    public readonly email: string | null,
    public readonly status: PartnerProfileStatus,
    public readonly components: ComponentList,
  ) {
    super();
    this.formattedName = getFormattedName(firstName, lastName)!;
    this.initials = getInitialsFromName(firstName, lastName)!;
  }

  get $typeId(): string {
    return CollaboratorProfile.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, CollaboratorProfile.SCHEMA_VERSION);
  }

  static create(args: {
    uniqueId: string,
    businessRef: BusinessModuleRef,
    firstName: string,
    lastName: string,
    phoneNumber: PhoneNumber,
    photo: PhotoReference | null,
    userId: string | null,
    pushNotificationToken: string | null,
    email: string | null,
    status: PartnerProfileStatus,
    components: ComponentList,
  }): CollaboratorProfile {
    return new CollaboratorProfile(
        args.uniqueId,
        args.businessRef,
        args.firstName,
        args.lastName,
        args.phoneNumber,
        args.photo,
        args.userId,
        args.pushNotificationToken,
        args.email,
        args.status,
        args.components,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): CollaboratorProfile {
    return new CollaboratorProfile(
        MapUtils.getString(map, 'uniqueId'),
        BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
        MapUtils.getString(map, 'firstName'),
        MapUtils.getString(map, 'lastName'),
        PhoneNumber.mustDecode(MapUtils.getString(map, 'phoneNumber')),
        context.fromDataOrNull<PhotoReference>(MapUtils.getRecordOrNull(map, 'photo')),
        MapUtils.getStringOrNull(map, 'userId'),
        MapUtils.getStringOrNull(map, 'pushNotificationToken'),
        MapUtils.getStringOrNull(map, 'email'),
        toEnumFromString(MapUtils.getString(map, '$status'), PartnerProfileStatus),
        ComponentList.deserialize(context, MapUtils.getRaw(map, 'components') as SerializedData[]),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'businessRef': this.businessRef.encode(),
      'firstName': this.firstName,
      'lastName': this.lastName,
      'phoneNumber': this.phoneNumber.encode(),
      'photo': context.toDataOrNull(this.photo),
      'userId': this.userId,
      'pushNotificationToken': this.pushNotificationToken,
      'email': this.email,
      'partnerRole': fromEnumAsInt(this.partnerRole),
      '$status': fromEnumAsString(this.status),
      'components': this.components.serialize(context),
    };
  }

  clone(): this {
    return this.copyWith();
  }

  copyWith({
    uniqueId = this.uniqueId,
    businessRef = cloneData(this.businessRef),
    firstName = this.firstName,
    lastName = this.lastName,
    phoneNumber = cloneData(this.phoneNumber),
    photo = cloneDataOrNull(this.photo),
    userId = this.userId,
    pushNotificationToken = this.pushNotificationToken,
    email = this.email,
    status = this.status,
    components = cloneData(this.components),
  }: {
    uniqueId?: string,
    businessRef?: BusinessModuleRef,
    firstName?: string,
    lastName?: string,
    phoneNumber?: PhoneNumber,
    photo?: PhotoReference | null,
    userId?: string | null,
    pushNotificationToken?: string | null,
    email?: string | null,
    status?: PartnerProfileStatus,
    components?: ComponentList,
  } = {}): this {
    return new CollaboratorProfile(
        uniqueId,
        businessRef,
        firstName,
        lastName,
        phoneNumber,
        photo,
        userId,
        pushNotificationToken,
        email,
        status,
        components,
    ) as this;
  }

  isSame(other: CollaboratorProfile): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.businessRef === this.businessRef &&
        other.firstName === this.firstName &&
        other.lastName === this.lastName &&
        isSameData(other.phoneNumber, this.phoneNumber) &&
        isSameData(other.photo, this.photo) &&
        other.userId === this.userId &&
        other.pushNotificationToken === this.pushNotificationToken &&
        other.email === this.email &&
        other.status === this.status &&
        other.components.isSame(this.components);
  }
}

export class CollaboratorProfileBuilder extends DataModelBuilder<CollaboratorProfile> {
  build(context: SerializationContext, map: SerializedData): CollaboratorProfile {
    return CollaboratorProfile.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: CollaboratorProfile): SerializedData | null {
    return { 'formattedName': model.formattedName };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(CollaboratorProfile.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(CollaboratorProfile.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version the `$status` field was added
    if(version < 2) {
      MapUtils.setIfNullOrUndefined(map, '$status', fromEnumAsString(PartnerProfileStatus.Live));
      version = 2;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
